export const LANDING_BANNER_ID = 'landing_banner';
export const BECOME_SELLER_HERO_ID = 'curated_marketplace';
export const SELLER_SLIDER_ID = 'seller_slider';

export const LANDING_HERO_VIDEOS = ['creator', 'athlete', 'performer'];

export const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const LAST_KEY = 'wearbyHeroVideoLast';
const VIDEO_BASE_PATH = '/static/videos';

// Holds the pick for this JS load so React remounts do not skip a clip.
let pageLoadVideo = null;

const isBrowser = () => typeof window !== 'undefined';

const readStorage = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeStorage = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch (e) {
    // Storage can be blocked in private mode.
  }
};

/**
 * Returns true when the viewport is at the landing-page desktop breakpoint.
 *
 * @returns {boolean}
 */
export const isDesktopViewport = () => {
  if (!isBrowser() || typeof window.matchMedia !== 'function') {
    return true;
  }
  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
};

/**
 * Returns true when the visitor has requested reduced motion.
 *
 * @returns {boolean}
 */
export const prefersReducedMotion = () => {
  if (!isBrowser() || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
};

/**
 * Subscribes to a media-query change. Call the returned function to unsubscribe.
 *
 * @param {string} query
 * @param {function(boolean): void} onChange
 * @returns {function(): void}
 */
export const subscribeToMediaQuery = (query, onChange) => {
  if (!isBrowser() || typeof window.matchMedia !== 'function') {
    return () => {};
  }

  const mediaQueryList = window.matchMedia(query);
  const listener = event => onChange(event.matches);

  if (typeof mediaQueryList.addEventListener === 'function') {
    mediaQueryList.addEventListener('change', listener);
    return () => mediaQueryList.removeEventListener('change', listener);
  }

  mediaQueryList.addListener(listener);
  return () => mediaQueryList.removeListener(listener);
};

/**
 * Picks a random landing-page clip on each full page load.
 * The pick is never the same as the previous page load.
 *
 * @returns {'creator' | 'athlete' | 'performer'}
 */
export const pickLandingHeroVideo = () => {
  const fallback = LANDING_HERO_VIDEOS[0];
  if (!isBrowser()) {
    return fallback;
  }

  if (pageLoadVideo && LANDING_HERO_VIDEOS.includes(pageLoadVideo)) {
    return pageLoadVideo;
  }

  const last = readStorage(window.localStorage, LAST_KEY);
  const pool = LANDING_HERO_VIDEOS.filter(video => video !== last);
  const choices = pool.length > 0 ? pool : LANDING_HERO_VIDEOS;
  const video = choices[Math.floor(Math.random() * choices.length)];

  rememberLandingHeroVideo(video);
  return video;
};

/**
 * Returns the next clip in the landing-page cycle.
 *
 * @param {'creator' | 'athlete' | 'performer'} current
 * @returns {'creator' | 'athlete' | 'performer'}
 */
export const nextLandingHeroVideo = current => {
  const currentIndex = LANDING_HERO_VIDEOS.indexOf(current);
  const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % LANDING_HERO_VIDEOS.length;
  return LANDING_HERO_VIDEOS[nextIndex];
};

/**
 * Remembers the clip that is on screen so remounts and the next refresh
 * stay in sync with what the visitor last saw.
 *
 * @param {'creator' | 'athlete' | 'performer'} video
 * @returns {void}
 */
export const rememberLandingHeroVideo = video => {
  if (!LANDING_HERO_VIDEOS.includes(video)) {
    return;
  }
  if (isBrowser()) {
    writeStorage(window.localStorage, LAST_KEY, video);
  }
  pageLoadVideo = video;
};

/**
 * Source URLs for one landing-page character.
 *
 * @param {'creator' | 'athlete' | 'performer'} character
 * @param {boolean} isDesktop
 * @returns {{ mp4: string, poster: string }}
 */
export const getLandingHeroSources = (character, isDesktop) => {
  const size = isDesktop ? 'desktop' : 'mobile';
  const base = `${VIDEO_BASE_PATH}/hero/${character}-${size}`;
  return {
    mp4: `${base}.mp4`,
    poster: `${base}.jpg`,
  };
};

/**
 * Source URLs for the Become a Seller video.
 *
 * @param {boolean} isDesktop
 * @returns {{ mp4: string, poster: string }}
 */
export const getSellerVideoSources = isDesktop => {
  const size = isDesktop ? 'desktop' : 'mobile';
  const base = `${VIDEO_BASE_PATH}/seller/wearby-edit-${size}`;
  return {
    mp4: `${base}.mp4`,
    poster: `${base}.jpg`,
  };
};
